import { ChevronDown } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ExternalLink, TrendingDown, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type ChangeResult = {
  node_id: string;
  last_name: string;
  prev_name: string;
  last_slug: string;
  prev_slug: string;
  last_description: string;
  prev_description: string;
  last_level: number;
  prev_level: number;
  last_url: string;
  prev_url: string;
  last_product_count: string;
  prev_product_count: string;
  last_title: string;
  prev_title: string;
  name_change: string;
  slug_change: string;
  description_change: string;
  level_change: string;
  url_change: string;
  product_count_change: string;
  title_change: string;
};

type ChangesTableProps = {
  changes?: ChangeResult[];
  isLoading: boolean;
};

const ProductCountDifference = ({ lastCount, prevCount }: { lastCount: string, prevCount: string }) => {
  const last = parseInt(lastCount) || 0;
  const prev = parseInt(prevCount) || 0;
  const difference = last - prev;

  if (difference === 0) return null;

  return (
    <span className={`inline-flex items-center gap-1 ${difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
      {difference > 0 ? (
        <TrendingUp className="h-4 w-4" />
      ) : (
        <TrendingDown className="h-4 w-4" />
      )}
      <span className="text-sm font-medium">
        {difference > 0 ? '+' : ''}{difference}
      </span>
    </span>
  );
};

const NameWithUrl = ({ name, url }: { name: string, url?: string }) => (
  url ? (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
    >
      {name}
      <ExternalLink className="h-4 w-4" />
    </a>
  ) : name
);

export const ChangesTable = ({ changes, isLoading }: ChangesTableProps) => {
  if (isLoading) {
    return <div className="text-center">Loading changes...</div>;
  }

  return (
    <div className="space-y-2">
      {changes?.map((change) => (
        <Collapsible key={change.node_id} className="border rounded-lg">
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/50">
            <div className="flex items-center gap-4">
              <ChevronDown className="h-4 w-4" />
              <div className="font-medium">
                <NameWithUrl name={change.last_name || change.prev_name} url={change.last_url || change.prev_url} />
              </div>
              <Badge variant="outline">
                {[
                  change.name_change === 'Changed' && 'Name',
                  change.title_change === 'Changed' && 'Title',
                  change.description_change === 'Changed' && 'Description',
                  change.level_change === 'Changed' && 'Level',
                  change.url_change === 'Changed' && 'URL',
                  change.product_count_change === 'Changed' && 'Product Count'
                ].filter(Boolean).join(', ')}
              </Badge>
            </div>
            {change.product_count_change === 'Changed' && (
              <ProductCountDifference 
                lastCount={change.last_product_count} 
                prevCount={change.prev_product_count}
              />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent className="p-4 pt-0 space-y-4">
            {change.name_change === 'Changed' && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Name</div>
                <div className="line-through text-gray-500">
                  <NameWithUrl name={change.prev_name} url={change.prev_url} />
                </div>
                <div>
                  <NameWithUrl name={change.last_name} url={change.last_url} />
                </div>
              </div>
            )}
            
            {change.title_change === 'Changed' && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Title</div>
                <div className="line-through text-gray-500">{change.prev_title}</div>
                <div>{change.last_title}</div>
              </div>
            )}
            
            {change.description_change === 'Changed' && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Description</div>
                <div className="line-through text-gray-500">{change.prev_description}</div>
                <div>{change.last_description}</div>
              </div>
            )}
            
            {change.level_change === 'Changed' && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Level</div>
                <div className="line-through text-gray-500">{change.prev_level}</div>
                <div>{change.last_level}</div>
              </div>
            )}
            
            {change.product_count_change === 'Changed' && (
              <div className="space-y-1">
                <div className="text-sm font-medium">Product Count</div>
                <div className="line-through text-gray-500">{change.prev_product_count}</div>
                <div className="flex items-center gap-2">
                  {change.last_product_count}
                  <ProductCountDifference 
                    lastCount={change.last_product_count} 
                    prevCount={change.prev_product_count}
                  />
                </div>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
};